import React, { useEffect, useMemo, useRef, useState } from "react";
import { AlignLeft, Calendar, ChevronDown, Pencil, Plus, Users, X, Trash2 } from "lucide-react";
import Avatar from "../ui/Avatar";
import NameTooltip from "../ui/NameTooltip";
import BaseModal from "../modals/BaseModal";
import ConfirmModal from "../modals/ConfirmModal";
import Button from "../forms/Button";
import Input from "../forms/Input";
import { boardTaskService, apiErrorMessage } from "../../api/taskBoardService";
import { BACKLOG_STATUS, PRIORITIES, STATUS_LABELS, TASK_STATUSES, TASK_TYPES } from "./constants";
import { editableTaskFields, roleIn, statusesFor } from "./boardState";
import { formatDate, toDateInput } from "./shared";
import TaskActivityPanel from "./TaskActivityPanel";
import TaskAttachments from "./TaskAttachments";
import MultiSelect from "./MultiSelect";
import TrelloEditor from "./TrelloEditor";
import { RichContent } from "./richText";

const fieldClass = "input-field w-full";
const Label = ({ children, required }) => (
  <span className="text-sm font-medium text-gray-700 dark:text-light-200">
    {children}
    {required && <span className="text-red-500 ml-1">*</span>}
  </span>
);

const FieldError = ({ message }) =>
  message ? <span className="text-sm text-red-500">{message}</span> : null;

const idOf = (v) => (v && typeof v === "object" ? v._id : v) || "";
const idsOf = (list) => (Array.isArray(list) ? list.map(idOf).filter(Boolean) : []);
/** Equality that treats id lists as sets. */
const sameValue = (a, b) =>
  Array.isArray(a) || Array.isArray(b)
    ? [...(a || [])].sort().join() === [...(b || [])].sort().join()
    : (a ?? null) === (b ?? null);

// Fields a Product Backlog task has. Priority and due date are optional there
// (and carried into the sprint); squad, sprint and assignees are set when the
// task is planned (PlanTaskModal).
const BACKLOG_FIELDS = ["title", "description", "taskType", "projects", "priority", "dueDate"];
const ALL_FIELDS = [
  ...BACKLOG_FIELDS,
  "squad",
  "sprint",
  "assignees",
  "status",
  "priority",
  "dueDate",
];


// ---------------- Task details (edit) building blocks ----------------

/** Trello-style block: small label above a 34px pill. */
const Meta = ({ label, children }) => (
  <div className="flex flex-col gap-1.5 min-w-0">
    <span className="text-[12px] text-faint whitespace-nowrap">{label}</span>
    {children}
  </div>
);

const pillBase =
  "relative inline-flex items-center gap-2 h-[34px] px-3 rounded-[10px] text-[13.5px] whitespace-nowrap max-w-full transition-colors";
const pillIdle = "bg-raise text-ink";
const pillHover = "hover:bg-hover hover:ring-1 hover:ring-line2 cursor-pointer focus-within:ring-2 focus-within:ring-accent-soft";

/** A pill that edits its field with a native <select> laid over it. */
const PillSelect = ({ label, value, onChange, disabled, children, tone = pillIdle, display }) => (
  <span className={`${pillBase} ${tone} ${disabled ? "opacity-80" : pillHover}`}>
    {display}
    {!disabled && <ChevronDown className="w-3.5 h-3.5 opacity-60" />}
    {!disabled && (
      <select
        aria-label={label}
        value={value}
        onChange={onChange}
        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
      >
        {children}
      </select>
    )}
  </span>
);

const PRIORITY_TONE = {
  critical: "bg-danger-soft text-danger",
  high: "bg-danger-soft text-danger",
  medium: "bg-warn-soft text-warn",
  low: pillIdle,
};

const STATUS_DOT = Object.fromEntries(TASK_STATUSES.map((st) => [st.value, st.accent]));

const toForm = (task, defaults) => ({
  title: task?.title || defaults.title || "",
  description: task?.description || defaults.description || "",
  taskType: task?.taskType || "feat",
  projects: task ? idsOf(task.projects) : defaults.projects || [],
  squad: idOf(task?.squad) || defaults.squad || "",
  sprint: idOf(task?.sprint) || defaults.sprint || "",
  assignees: idsOf(task?.assignees),
  status: task?.status || defaults.status || BACKLOG_STATUS,
  priority: task?.priority || "",
  dueDate: toDateInput(task?.dueDate),
});

// Form value → API value ("" clears an optional relation).
const toPayload = (form, fields) => {
  const all = {
    ...form,
    title: form.title.trim(),
    squad: form.squad || null,
    sprint: form.sprint || null,
    priority: form.priority || null,
    dueDate: form.dueDate ? new Date(form.dueDate).toISOString() : null,
  };
  return Object.fromEntries(fields.map((f) => [f, all[f]]));
};

// Creating: optional priority / due date are simply left out when empty.
const withoutEmptyOptionals = (payload) =>
  Object.fromEntries(Object.entries(payload).filter(([k, v]) => !(["priority", "dueDate"].includes(k) && v === null)));

/**
 * Create / edit a task. New tasks go to the Product Backlog, so creating (and
 * editing a backlog task) shows only title, description, type and project.
 * Planned tasks show every field, locked per role. Editing sends only changed
 * fields plus the task `version`, so a concurrent change yields a conflict
 * instead of being lost.
 */
const TaskFormModal = ({
  isOpen,
  onClose,
  task,
  defaults = {},
  options,
  onSaved,
  onConflict,
  onDeleted,
  subscribeActivity,
}) => {
  const isEdit = Boolean(task);
  // Product Backlog tasks only have title/description/type/project. Creating
  // from any other column ("+" on that column) asks for everything up front.
  const backlogMode = isEdit
    ? task.status === BACKLOG_STATUS
    : (defaults.status || BACKLOG_STATUS) === BACKLOG_STATUS;
  const plannedCreate = !isEdit && !backlogMode;
  const fields = backlogMode ? BACKLOG_FIELDS : ALL_FIELDS;
  const { access } = options;
  const [form, setForm] = useState(() => toForm(task, defaults));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmQa, setConfirmQa] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  // Value when editing started, so Cancel can restore it.
  const descriptionBefore = useRef("");
  const startDescription = () => {
    descriptionBefore.current = form.description;
    setEditingDescription(true);
  };
  // Permanent delete is for high_potit admins only (enforced by the API too).
  const canDelete = isEdit && access.isAdmin;

  useEffect(() => {
    if (isOpen) {
      setForm(toForm(task, defaults));
      setErrors({});
      setServerError("");
      setEditingDescription(false);
    }
  }, [isOpen, task?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  const editable = useMemo(
    () => (isEdit ? editableTaskFields(access, task) : null),
    [isEdit, access, task],
  );
  const canEdit = (field) => !isEdit || editable.has(field);
  const readOnly = isEdit && !fields.some((f) => editable.has(f));

  const selectedSquad = options.squads.find((s) => s._id === form.squad);
  const assigneeOptions = useMemo(
    () => [
      ...new Map(
        [...(selectedSquad?.members || []).map((m) => m.employee), ...(options.admins || [])]
          .filter(Boolean)
          .map((e) => [e._id, e]),
      ).values(),
    ],
    [selectedSquad, options.admins],
  );
  // Squad leads create tasks only in the squads they lead.
  const squadOptions =
    !isEdit && !access.isAdmin
      ? options.squads.filter((sq) => roleIn(access, sq._id) === "SQUAD_LEAD")
      : options.squads;
  const statusOptions = statusesFor(access).filter(
    (st) => !plannedCreate || st.value !== BACKLOG_STATUS,
  );
  const sprintOptions = options.sprints.filter(
    (s) => s.status !== "completed" || s._id === form.sprint,
  );

  const set = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => {
      const next = { ...f, [key]: value };
      // Changing squad drops assignees who are not in the new squad.
      if (key === "squad") {
        const squad = options.squads.find((s) => s._id === value);
        const members = new Set([...(squad?.members || []).map((m) => idOf(m.employee)), ...(options.admins || []).map(idOf)]);
        next.assignees = f.assignees.filter((id) => members.has(id));
      }
      return next;
    });
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.title.trim()) er.title = "Title is required";
    if (plannedCreate) {
      // The squad is optional for admins; squad leads can only create tasks in a squad they lead.
      if (!form.squad && !access.isAdmin) er.squad = "Choose a squad";
      if (!form.sprint) er.sprint = "Sprint is required";
      if (!form.priority) er.priority = "Priority is required";
      if (!form.dueDate) er.dueDate = "Due date is required";
    }
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setServerError("");
    try {
      const payload = toPayload(form, fields);
      let res;
      if (isEdit) {
        const original = toPayload(toForm(task, {}), fields);
        const changes = Object.fromEntries(
          Object.entries(payload).filter(([k, v]) => !sameValue(original[k], v)),
        );
        if (!Object.keys(changes).length) return onClose();
        res = await boardTaskService.update(task._id, {
          ...changes,
          version: task.version,
        });
      } else {
        res = await boardTaskService.create({
          ...withoutEmptyOptionals(payload),
          // Hidden link set when the task is created from an employee request.
          ...(defaults?.employeeRequest ? { employeeRequest: defaults.employeeRequest } : {}),
        });
      }
      onSaved(
        res.data,
        isEdit
          ? "Task updated"
          : backlogMode
            ? "Task added to the Product Backlog"
            : "Task created",
      );
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data?.current) {
        onConflict?.(err.response.data.current);
        setForm(toForm(err.response.data.current, {}));
        setServerError(
          "Someone else changed this task. The latest version is now shown; review and save again.",
        );
      } else {
        setServerError(apiErrorMessage(err, "Could not save the task"));
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setConfirmDelete(false);
    setSaving(true);
    setServerError("");
    try {
      await boardTaskService.remove(task._id);
      onDeleted(task._id);
    } catch (err) {
      setServerError(apiErrorMessage(err, "Could not delete the task"));
      setSaving(false);
    }
  };

  // Removing a QA rejection is for high_potit admins only (enforced by the API too). It changes the task on
  // the server, so the open form takes the updated task (new version) instead of keeping a stale snapshot.
  const handleClearQa = async () => {
    setConfirmQa(false);
    setSaving(true);
    setServerError("");
    try {
      const res = await boardTaskService.clearQaRejections(task._id);
      onConflict?.(res.data); // applies the server's task to the board and re-opens this form on it
    } catch (err) {
      setServerError(apiErrorMessage(err, "Could not remove the QA rejection"));
    } finally {
      setSaving(false);
    }
  };

  // People shown in the Members block: task's populated assignees plus the
  // squad's members (for anyone newly picked).
  const people = new Map(
    [...(task?.assignees || []).filter((a) => a && typeof a === "object"), ...assigneeOptions].map((p) => [idOf(p), p]),
  );
  const sprintOf = options.sprints.find((sp) => sp._id === form.sprint);
  const statusLabel = STATUS_LABELS[form.status] || form.status;

  const footer = (
    <div className="flex w-full flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
      {canDelete && (
        <Button variant="danger" onClick={() => setConfirmDelete(true)} disabled={saving} className="sm:mr-auto">
          <Trash2 className="w-4 h-4" /> Delete
        </Button>
      )}
      <Button variant="secondary" onClick={onClose} disabled={saving}>
        Cancel
      </Button>
      <Button onClick={handleSubmit} disabled={saving || readOnly}>
        {saving ? "Saving…" : isEdit ? "Save changes" : "Create task"}
      </Button>
    </div>
  );

  if (isEdit) {
    const statusEditable = !backlogMode && canEdit("status");
    const cover = (
      <div className="relative h-[92px] bg-grad overflow-hidden">
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              "linear-gradient(var(--on-accent) 1px, transparent 1px), linear-gradient(90deg, var(--on-accent) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <div aria-hidden="true" className="absolute inset-x-0 bottom-0 h-10 bg-gradient-to-b from-transparent to-black/20" />
        <div className="relative h-full px-5 sm:px-7 flex items-center gap-3">
          <h2 className="font-display text-[20px] font-semibold text-on-accent whitespace-nowrap">Task #{task.number}</h2>
          <span
            className={`relative inline-flex items-center gap-2 h-9 px-3 rounded-xl border border-white/60 bg-white/55 backdrop-blur-md text-on-accent text-[13.5px] font-medium min-w-0 ${
              statusEditable ? "cursor-pointer hover:bg-white/70" : ""
            }`}
          >
            <span className={`w-2 h-2 rounded-full shrink-0 ${STATUS_DOT[form.status] || "bg-faint"}`} />
            <span className="truncate">
              {sprintOf ? `${sprintOf.name} · ` : ""}
              {statusLabel}
            </span>
            {statusEditable && <ChevronDown className="w-4 h-4 shrink-0" />}
            {statusEditable && (
              <select
                aria-label="Status"
                value={form.status}
                onChange={set("status")}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              >
                {statusOptions.map((st) => (
                  <option key={st.value} value={st.value}>
                    {st.label}
                  </option>
                ))}
              </select>
            )}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="ml-auto shrink-0 w-9 h-9 rounded-xl border border-white/60 bg-white/55 backdrop-blur-md text-on-accent flex items-center justify-center hover:bg-white/75"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );

    const priorityMeta = (
      <Meta label="Priority">
        <PillSelect
          label="Priority"
          value={form.priority}
          onChange={set("priority")}
          disabled={!canEdit("priority")}
          tone={PRIORITY_TONE[form.priority] || pillIdle}
          display={PRIORITIES.find((x) => x.value === form.priority)?.label || <span className="text-faint">None</span>}
        >
          <option value="">Select priority…</option>
          {PRIORITIES.map((pr) => (
            <option key={pr.value} value={pr.value}>
              {pr.label}
            </option>
          ))}
        </PillSelect>
      </Meta>
    );
    const dueMeta = (
      <Meta label="Due date">
        <span className={`${pillBase} ${pillIdle} ${canEdit("dueDate") ? pillHover : "opacity-80"}`}>
          <Calendar className="w-3.5 h-3.5 text-muted" />
          {form.dueDate ? formatDate(form.dueDate, { day: "numeric", month: "short", year: "numeric" }) : <span className="text-faint">None</span>}
          {canEdit("dueDate") && (
            <input
              type="date"
              aria-label="Due date"
              value={form.dueDate}
              onChange={set("dueDate")}
              onClick={(e) => e.currentTarget.showPicker?.()}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          )}
        </span>
      </Meta>
    );

    return (
      <>
        <BaseModal isOpen={isOpen} onClose={onClose} size="2xl" header={cover} bodyClassName="p-0" footer={footer}>
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(300px,360px)] lg:h-[calc(90vh-170px)]">
            <div className="min-w-0 px-5 sm:px-8 py-7 flex flex-col gap-[26px] lg:overflow-y-auto">
              {serverError && (
                <div role="alert" className="text-sm p-3 rounded-xl border border-danger/30 bg-danger-soft text-danger">
                  {serverError}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <input
                  aria-label="Title"
                  value={form.title}
                  onChange={set("title")}
                  maxLength={200}
                  disabled={!canEdit("title")}
                  className="w-full bg-transparent font-display text-[24px] sm:text-[28px] leading-tight font-semibold tracking-[-0.02em] text-ink rounded-lg px-1.5 -mx-1.5 py-0.5 border border-transparent hover:border-line2 focus:border-accent-line focus:outline-none focus:ring-2 focus:ring-accent-soft disabled:hover:border-transparent"
                />
                {errors.title && <p className="text-sm text-danger mt-1">{errors.title}</p>}
                <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
              </form>

              <div className="flex flex-wrap gap-x-6 gap-y-4">
                  <Meta label="Members">
                    <MultiSelect
                      ariaLabel="Assignees"
                      value={form.assignees}
                      onChange={set("assignees")}
                      disabled={backlogMode || !canEdit("assignees")}
                      options={assigneeOptions.map((e) => ({ value: e._id, label: e.fullNameEn || e.nickname }))}
                      renderTrigger={({ toggle, disabled }) => (
                        <div className="flex items-center h-[34px]">
                          <div className="flex -space-x-2">
                            {form.assignees.map((id) => {
                              const p = people.get(id);
                              return (
                                <NameTooltip key={id} name={p?.fullNameEn || p?.nickname}>
                                  <Avatar src={p?.photo} name={p?.fullNameEn || p?.nickname || "?"} size="sm" className="ring-2 ring-[var(--panel-solid)]" />
                                </NameTooltip>
                              );
                            })}
                          </div>
                          {form.assignees.length === 0 && <span className="text-[13.5px] text-faint mr-2">Unassigned</span>}
                          {!disabled && (
                            <button
                              type="button"
                              onClick={toggle}
                              aria-label="Edit assignees"
                              className={`${form.assignees.length ? "ml-1.5" : ""} w-8 h-8 rounded-full border border-dashed border-line2 text-muted flex items-center justify-center hover:text-accent hover:border-accent-line`}
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      )}
                    />
                    {errors.assignees && <span className="text-xs text-danger">{errors.assignees}</span>}
                  </Meta>

                  <Meta label="Squad">
                    <PillSelect
                      label="Squad"
                      value={form.squad}
                      onChange={set("squad")}
                      disabled={backlogMode || !canEdit("squad")}
                      display={
                        <>
                          <Users className="w-3.5 h-3.5 text-muted" />
                          {options.squads.find((x) => x._id === form.squad)?.name || <span className="text-faint">None</span>}
                        </>
                      }
                    >
                      <option value="">No squad</option>
                      {squadOptions.map((sq) => (
                        <option key={sq._id} value={sq._id}>
                          {sq.name}
                        </option>
                      ))}
                    </PillSelect>
                  </Meta>

                  <Meta label="Sprint">
                    <PillSelect
                      label="Sprint"
                      value={form.sprint}
                      onChange={set("sprint")}
                      disabled={backlogMode || !canEdit("sprint")}
                      display={
                        <>
                          <span className={`w-2 h-2 rounded-full ${sprintOf?.status === "active" ? "bg-ok" : "bg-faint"}`} />
                          {sprintOf?.name || <span className="text-faint">None</span>}
                        </>
                      }
                    >
                      <option value="">Select sprint…</option>
                      {sprintOptions.map((sp) => (
                        <option key={sp._id} value={sp._id}>{`${sp.name} (${sp.status})`}</option>
                      ))}
                    </PillSelect>
                  </Meta>


                  {priorityMeta}

                  {dueMeta}

                  {(task.deadlineMissed || task.qaRejectedCount > 0) && (
                    <Meta label="Delivery">
                      <span className="flex flex-wrap gap-2">
                        {task.deadlineMissed && (
                          <span className={`${pillBase} bg-danger-soft text-danger`} title="Kept even after the task is done">
                            {task.status === "done"
                              ? `Finished ${task.daysLate || 1}d late`
                              : `Overdue ${task.daysLate || 1}d`}
                          </span>
                        )}
                        {task.qaRejectedCount > 0 && (
                          <span
                            className={`${pillBase} bg-danger-soft text-danger`}
                            title={task.lastQaRejectedAt ? `Last rejected ${formatDate(task.lastQaRejectedAt)}` : undefined}
                          >
                            QA rejected ×{task.qaRejectedCount}
                            {access.isAdmin && (
                              <button
                                type="button"
                                onClick={() => setConfirmQa(true)}
                                disabled={saving}
                                title="Remove the QA rejection (admins only)"
                                aria-label="Remove the QA rejection"
                                className="ml-1 -mr-1 w-5 h-5 rounded-full grid place-items-center hover:bg-danger/20 disabled:opacity-50"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            )}
                          </span>
                        )}
                      </span>
                    </Meta>
                  )}
                </div>

              <div className="flex flex-wrap gap-x-6 gap-y-4">
                <Meta label="Type">
                  <PillSelect
                    label="Type"
                    value={form.taskType}
                    onChange={set("taskType")}
                    disabled={!canEdit("taskType")}
                    display={
                      <>
                        <span className={`font-mono text-[11px] px-1.5 py-px rounded-md ${form.taskType === "fix" ? "bg-danger-soft text-danger" : form.taskType === "improvement" ? "bg-warn-soft text-warn" : "bg-accent-soft text-accent-ink"}`}>
                          {form.taskType}
                        </span>
                        {TASK_TYPES.find((t) => t.value === form.taskType)?.description}
                      </>
                    }
                  >
                    {TASK_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{`${t.label} — ${t.description}`}</option>
                    ))}
                  </PillSelect>
                </Meta>

                <Meta label="Projects">
                  <MultiSelect
                    ariaLabel="Projects"
                    value={form.projects}
                    onChange={set("projects")}
                    disabled={!canEdit("projects")}
                    options={options.projects.map((p) => ({ value: p._id, label: p.name, color: p.color }))}
                    renderTrigger={({ toggle, selected, disabled }) => (
                      <button
                        type="button"
                        onClick={toggle}
                        disabled={disabled}
                        aria-label="Edit projects"
                        className="flex flex-wrap items-center gap-2 text-left disabled:cursor-default"
                      >
                        {selected.length === 0 ? (
                          <span className={`${pillBase} ${pillIdle} text-faint ${disabled ? "" : pillHover}`}>No project</span>
                        ) : (
                          selected.map((o) => (
                            <span key={o.value} className={`${pillBase} ${pillIdle} ${disabled ? "" : pillHover}`}>
                              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: o.color || "var(--accent)" }} />
                              <span className="truncate">{o.label}</span>
                            </span>
                          ))
                        )}
                        {!disabled && selected.length > 0 && (
                          <span className="w-[34px] h-[34px] rounded-[10px] border border-dashed border-line2 text-muted flex items-center justify-center hover:text-accent hover:border-accent-line">
                            <Plus className="w-4 h-4" />
                          </span>
                        )}
                      </button>
                    )}
                  />
                </Meta>
              </div>

              {backlogMode && (
                <p className="text-[13px] text-faint -mt-2">Product Backlog — members, squad and sprint are set when the task is planned (drag it to Sprint Backlog). Priority and due date are optional here.</p>
              )}
              {!backlogMode && editable.size > 0 && editable.size < 10 && (
                <p className="text-[12.5px] text-faint -mt-2">
                  {editable.size === 1
                    ? "You can change the status of this task. Other fields are managed by the squad lead."
                    : "Type, squad, sprint and due date can only be changed by an admin."}
                </p>
              )}

              <section>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="flex items-center gap-2.5 font-display text-[19px] font-semibold text-ink whitespace-nowrap">
                    <AlignLeft className="w-5 h-5 text-muted" /> Description
                  </h3>
                  {canEdit("description") && !editingDescription && (
                    <button type="button" className="btn-ghost !h-9 !px-3 text-[13px]" onClick={() => (editingDescription ? setEditingDescription(false) : startDescription())}>
                      {editingDescription ? "Done" : (<><Pencil className="w-3.5 h-3.5" /> Edit</>)}
                    </button>
                  )}
                </div>
                {editingDescription ? (
                  <div>
                    <TrelloEditor
                      value={form.description}
                      onChange={(html) => set("description")(html)}
                      placeholder="Add a more detailed description…"
                      autoFocus
                      minHeight={180}
                      ariaLabel="Description"
                    />
                    <div className="mt-2.5 flex items-center gap-2">
                      <button type="button" className="btn-primary !h-9 text-[13px]" onClick={() => setEditingDescription(false)}>
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-ghost !h-9 text-[13px]"
                        onClick={() => {
                          set("description")(descriptionBefore.current);
                          setEditingDescription(false);
                        }}
                      >
                        Cancel
                      </button>
                      <span className="text-[12px] text-faint">Saved with the task when you press Save changes.</span>
                    </div>
                  </div>
                ) : (
                  <div
                    role={canEdit("description") ? "button" : undefined}
                    tabIndex={canEdit("description") ? 0 : undefined}
                    onClick={() => canEdit("description") && startDescription()}
                    onKeyDown={(e) => e.key === "Enter" && canEdit("description") && startDescription()}
                    className={`rounded-[14px] bg-raise border border-line px-5 py-4 text-[15px] leading-[1.7] ${
                      form.description ? "text-ink" : "text-faint italic"
                    } ${canEdit("description") ? "cursor-text hover:border-line2" : ""}`}
                  >
                    {form.description ? (
                      <RichContent value={form.description} />
                    ) : canEdit("description") ? (
                      "Add a more detailed description…"
                    ) : (
                      "No description yet."
                    )}
                  </div>
                )}
              </section>

              <TaskAttachments task={task} access={access} subscribeActivity={subscribeActivity} />
            </div>

            <aside className="min-w-0 bg-panel2 border-t lg:border-t-0 lg:border-l border-line px-5 py-6 lg:overflow-y-auto">
              <TaskActivityPanel task={task} access={access} subscribeActivity={subscribeActivity} />
            </aside>
          </div>
        </BaseModal>

        <ConfirmModal
          isOpen={confirmDelete}
          onClose={() => setConfirmDelete(false)}
          onConfirm={handleDelete}
          title={`Delete task #${task?.number ?? ""}?`}
          message="This permanently deletes the task for everyone. It cannot be undone."
          confirmText="Delete permanently"
        />

        <ConfirmModal
          isOpen={confirmQa}
          onClose={() => setConfirmQa(false)}
          onConfirm={handleClearQa}
          title="Remove the QA rejection?"
          message={`This clears the QA rejection${task?.qaRejectedCount > 1 ? `s (×${task.qaRejectedCount})` : ""} from task #${task?.number ?? ""}. The task's status is not changed, and the removal is recorded in its history.`}
          confirmText="Remove rejection"
        />
      </>
    );
  }

  return (
    <>
      <BaseModal
        isOpen={isOpen}
        onClose={onClose}
        // Existing tasks use a Trello-style two-column layout: details on the
        // left, "Comments and Activity" on the right.
        size={isEdit ? "2xl" : "lg"}
        title={isEdit ? `Task #${task.number}` : "New task"}
        subtitle={
          plannedCreate
            ? "Squad, sprint, due date and priority are required. Assignees can be set later."
            : !isEdit
              ? "New tasks start in the Product Backlog. Priority and due date are optional; squad, sprint and assignees are set when the task is planned."
              : backlogMode
                ? "Product Backlog — drag the task to Sprint Backlog to plan it."
                : undefined
        }
        footer={footer        }
      >
        <div
          className={
            isEdit
              ? "grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(320px,400px)] gap-6"
              : ""
          }
        >
          <div className="min-w-0 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              {serverError && (
                <div
                  role="alert"
                  className="text-sm p-3 rounded-xl border border-danger/30 bg-danger-soft text-danger"
                >
                  {serverError}
                </div>
              )}

              <Input
                label="Title"
                required
                value={form.title}
                onChange={set("title")}
                error={errors.title}
                maxLength={200}
                autoFocus={!isEdit}
                disabled={!canEdit("title")}
              />

              <div className="flex flex-col gap-1.5">
                <Label>Description</Label>
                <TrelloEditor
                  value={form.description}
                  onChange={(html) => set("description")(html)}
                  placeholder="Add a more detailed description…"
                  disabled={!canEdit("description")}
                  minHeight={120}
                  ariaLabel="Description"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="flex flex-col gap-1.5">
                  <Label>Type</Label>
                  <select
                    value={form.taskType}
                    onChange={set("taskType")}
                    disabled={!canEdit("taskType")}
                    className={fieldClass}
                  >
                    {TASK_TYPES.map((t) => (
                      <option
                        key={t.value}
                        value={t.value}
                      >{`${t.label} — ${t.description}`}</option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1.5">
                  <Label>Projects</Label>
                  <MultiSelect
                    ariaLabel="Projects"
                    placeholder="No project"
                    value={form.projects}
                    onChange={set("projects")}
                    disabled={!canEdit("projects")}
                    options={options.projects.map((p) => ({ value: p._id, label: p.name, color: p.color }))}
                  />
                </label>

                {!backlogMode && (
                  <>
                    <label className="flex flex-col gap-1.5">
                      <Label required={plannedCreate && !access.isAdmin}>Squad</Label>
                      <select
                        value={form.squad}
                        onChange={set("squad")}
                        disabled={!canEdit("squad")}
                        className={fieldClass}
                      >
                        <option value="">No squad</option>
                        {squadOptions.map((s) => (
                          <option key={s._id} value={s._id}>
                            {s.name}
                          </option>
                        ))}
                      </select>
                      <FieldError message={errors.squad} />
                    </label>

                    <label className="flex flex-col gap-1.5">
                      <Label required={plannedCreate}>Sprint</Label>
                      <select
                        value={form.sprint}
                        onChange={set("sprint")}
                        disabled={!canEdit("sprint")}
                        className={fieldClass}
                      >
                        <option value="">Select sprint…</option>
                        {sprintOptions.map((s) => (
                          <option
                            key={s._id}
                            value={s._id}
                          >{`${s.name} (${s.status})`}</option>
                        ))}
                      </select>
                      <FieldError message={errors.sprint} />
                    </label>

                    <label className="flex flex-col gap-1.5">
                      <Label>Assignees</Label>
                      <MultiSelect
                        ariaLabel="Assignees"
                        placeholder={form.squad ? "Unassigned" : "High_potit members (no squad)"}
                        value={form.assignees}
                        onChange={set("assignees")}
                        disabled={!canEdit("assignees")}
                        options={assigneeOptions.map((e) => ({ value: e._id, label: e.fullNameEn || e.nickname }))}
                      />
                      <FieldError message={errors.assignees} />
                    </label>

                    <label className="flex flex-col gap-1.5">
                      <Label>Status</Label>
                      <select
                        value={form.status}
                        onChange={set("status")}
                        disabled={!canEdit("status")}
                        className={fieldClass}
                      >
                        {statusOptions.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </label>

                  </>
                )}

                <label className="flex flex-col gap-1.5">
                  <Label required={plannedCreate}>Priority</Label>
                  <select
                    value={form.priority}
                    onChange={set("priority")}
                    disabled={!canEdit("priority")}
                    className={fieldClass}
                  >
                    <option value="">Select priority…</option>
                    {PRIORITIES.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                  <FieldError message={errors.priority} />
                </label>

                <Input
                  label="Due date"
                  type="date"
                  required={plannedCreate}
                  value={form.dueDate}
                  onChange={set("dueDate")}
                  error={errors.dueDate}
                  disabled={!canEdit("dueDate")}
                />
              </div>

              {isEdit &&
                !backlogMode &&
                editable.size > 0 &&
                editable.size < 10 && (
                  <p className="text-xs text-gray-500 dark:text-light-500">
                    {editable.size === 1
                      ? "You can change the status of this task. Other fields are managed by the squad lead."
                      : "Type, squad, sprint and due date can only be changed by an admin."}
                  </p>
                )}
              {/* Lets Enter submit from inputs. */}
              <button
                type="submit"
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
              />
            </form>
            {isEdit && (
              <TaskAttachments
                task={task}
                access={access}
                subscribeActivity={subscribeActivity}
              />
            )}
          </div>
          {isEdit && (
            // Stays visible while the details scroll on large screens; stacks
            // under the details on narrow screens.
            <aside className="min-w-0 border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-dark-100 pt-6 lg:pt-0 lg:pl-6 lg:sticky lg:top-0 lg:self-start lg:max-h-[calc(90vh-12rem)] lg:overflow-y-auto">
              <TaskActivityPanel
                task={task}
                access={access}
                subscribeActivity={subscribeActivity}
              />
            </aside>
          )}
        </div>
      </BaseModal>

      <ConfirmModal
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={`Delete task #${task?.number ?? ""}?`}
        message="This permanently deletes the task for everyone. It cannot be undone."
        confirmText="Delete permanently"
      />
    </>
  );
};

export default TaskFormModal;
